/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {createPortal} from 'react-dom'
import {useTranslation} from 'react-i18next'
import {UpsellImage} from 'components/UpsellImage'

interface PackageUpsellSlideOverProps {
    isOpen: boolean
    title: string
    descriptionHtml?: string | null
    imageSrc?: string | null
    groupLabel?: string | null
    unitPrice: React.ReactNode
    totalPrice: React.ReactNode
    inCartCount: number
    detailsSlot: React.ReactNode
    customTextSlot: React.ReactNode
    actionsSlot: React.ReactNode
    onClose: () => void
    portalPosition?: Element | null
}

const PackageUpsellSlideOver = ({
    isOpen,
    title,
    descriptionHtml,
    imageSrc,
    groupLabel,
    unitPrice,
    totalPrice,
    inCartCount,
    detailsSlot,
    customTextSlot,
    actionsSlot,
    onClose,
    portalPosition = typeof document !== 'undefined' ? document.body : null,
}: PackageUpsellSlideOverProps) => {
    const { t } = useTranslation()

    React.useEffect(() => {
        if (!isOpen) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !event.defaultPrevented) {
                // Own dismissal before the underlying modal's document handler.
                event.preventDefault()
                event.stopPropagation()
                onClose()
            }
        }

        window.addEventListener('keydown', onKeyDown, true)
        return () => window.removeEventListener('keydown', onKeyDown, true)
    }, [isOpen, onClose])

    React.useEffect(() => {
        if (!isOpen || typeof document === 'undefined') return

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [isOpen])

    if (!isOpen || !portalPosition) {
        return null
    }

    const slideOverContent = (
        <div className="PackageUpsellSlideOver fixed inset-0" style={{ zIndex: 1400 }}>
            <button
                aria-label={t('Close')}
                className="absolute inset-0 bg-slate-950/40 backdrop-blur-[2px]"
                type="button"
                onClick={onClose}
            />

            <div
                className="absolute inset-x-0 bottom-0 mx-auto max-w-xl rounded-t-4xl bg-white dark:bg-gray-900 p-5 shadow-2xl ring-1 ring-slate-200 dark:ring-slate-700 animate-in slide-in-from-bottom duration-300 max-h-[90dvh] flex flex-col"
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="mx-auto mb-5 h-1.5 w-12 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0" />

                <div className="mb-5 flex items-start gap-4 shrink-0">
                    {imageSrc && (
                        <div className="shrink-0 size-14 overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-800">
                            <UpsellImage alt={title} className="w-full h-full object-cover" src={imageSrc} />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        {groupLabel && (
                            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">
                                {groupLabel}
                            </p>
                        )}
                        <h2 className="mt-0.5 text-xl font-bold text-slate-950 dark:text-slate-100 leading-tight">
                            {title}
                        </h2>
                        {descriptionHtml && (
                            <p
                                className="mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-3"
                                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                            />
                        )}
                    </div>
                    <button
                        className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                        type="button"
                        onClick={onClose}
                    >
                        {t('Close')}
                    </button>
                </div>

                <div className="mb-4 flex items-center justify-between rounded-2xl bg-slate-50 dark:bg-slate-800 px-4 py-3 shrink-0">
                    <div>
                        <span className="text-sm text-slate-600 dark:text-slate-400">{t('Price per item')}</span>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {t('In cart')}: {inCartCount}
                        </p>
                    </div>
                    <div className="text-right">
                        <span className="text-lg font-bold text-slate-950 dark:text-slate-100 block">{unitPrice}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                            {t('Total')}: {totalPrice}
                        </span>
                    </div>
                </div>

                <div className="mb-4 shrink-0">{detailsSlot}</div>
                <div className="mb-5 shrink-0">{customTextSlot}</div>
                <div className="flex-1 min-h-0" />
                <div className="flex gap-3 shrink-0">{actionsSlot}</div>
            </div>
        </div>
    )

    if (typeof document === 'undefined') {
        return slideOverContent
    }

    return createPortal(slideOverContent, portalPosition!)
}

export default PackageUpsellSlideOver
